import React from 'react';
import { ColorSelector } from '@ajat/design-system/Configurator';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../../shared';
import swatchLight from '@ajat/design-system/assets/crownstudent/configurator/colors/light-gray.png';
import swatchWine from '@ajat/design-system/assets/crownstudent/configurator/colors/wine.png';
import swatchNavy from '@ajat/design-system/assets/crownstudent/configurator/colors/navy.png';
import swatchEco from '@ajat/design-system/assets/crownstudent/configurator/colors/eco.png';

export const meta = {
  component: ColorSelector,
  title: 'Color Selector',
  group: 'Configurator',
  importPath: '@ajat/design-system/Configurator',
  figmaNode: '3812:1202',
  description:
    'Round 44px swatch with a tooltip naming the colour. Hover effects and the tooltip are gated behind `@media (hover: hover)`, so touch devices get neither — never re-enable them for mobile.',
  props: [
    ['color', 'string', "'var(--color-gray-3)'", 'Any CSS colour — hex, rgb, or a token'],
    ['image', 'string', '—', 'Photo swatch — covers the circle over the colour (the fallback while it loads)'],
    ['name', 'string', '—', 'Colour name shown in the tooltip; omit it and no tooltip renders'],
    ['selected', 'bool', 'false', 'Selected state — success-green ring'],
    ['error', 'bool', 'false', 'Error state — error-red ring'],
    ['onClick', 'func', '—', 'Click handler'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'Colour picker',
      code: `import { ColorSelector } from '@ajat/design-system/Configurator';

{colors.map((c, index) => (
  <ColorSelector
    key={c.id}
    color={c.hex}
    name={c.name}
    selected={index === selectedColorIdx}
    onClick={() => setSelectedColorIdx(index)}
  />
))}

// Colour selection is index-based — never pre-filter the
// array to hide sold-out options, or the indices shift.`,
    },
  ],
};

export default function ColorSelectorPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States" hint="hover for the tooltip and shadow">
        <DemoRow align="center">
          <Demo caption="DEFAULT" center><ColorSelector color="var(--color-gray-3)" name="Light Grey" /></Demo>
          <Demo caption="SELECTED" center><ColorSelector color="var(--color-gray-3)" name="Light Grey" selected /></Demo>
          <Demo caption="ERROR" center><ColorSelector color="var(--color-gray-3)" name="Light Grey" error /></Demo>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Palette row">
        <div className="flex flex-wrap items-center gap-[var(--space-ui-small-l)]">
          <ColorSelector color="#080c0a" name="Black" selected />
          <ColorSelector color="#293a35" name="Forest" />
          <ColorSelector color="#1B365D" name="Navy" />
          <ColorSelector color="#8B1A1A" name="Burgundy" />
          <ColorSelector color="#D5D7D6" name="Heather Grey" />
          <ColorSelector color="#FFFFFF" name="White" />
        </div>
      </DemoCard>

      <DemoCard title="Photo swatches" hint="image over the colour fallback (Crown Student cap colours)">
        <div className="flex items-center gap-[var(--space-ui-small-l)]">
          <ColorSelector color="var(--color-gray-1)" image={swatchLight} name="Ljusgrå" />
          <ColorSelector color="#6e1b23" image={swatchWine} name="Vinröd" selected />
          <ColorSelector color="#243a6b" image={swatchNavy} name="Marinblå" />
          <ColorSelector color="var(--color-gray-0)" image={swatchEco} name="Eko" />
        </div>
      </DemoCard>

      <DemoCard title="Without a name" hint="no name, no tooltip">
        <div className="flex items-center gap-[var(--space-ui-small-l)]">
          <ColorSelector color="#28e280" />
          <ColorSelector color="#e2bb5c" selected />
          <ColorSelector color="#b50b0b" error />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
